import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { includes } from 'lodash';

import { DisplayStyle, WatchBadge, WatchBadgeListing } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { Item2AvailableActionsData, core_apimessages_UserActionName, CreateUserActionMeData, core_apimessages_UserActionStatus } from '../schemas/rql';
import { withCreateUserActionMutation, CoreUserActionsCreateMutationFn, useCreateUserAction } from '@reverbdotcom/commons/src/user_actions/create_action_mutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import { availableOfferButtonAction } from '@reverbdotcom/commons/src/offers/create_offer_button';

export const Item2AvailableActionsFragment = gql`
  fragment Item2AvailableActionsData on core_apimessages_AvailableAction {
    id
    name
    status
  }
`;

const WATCH_BADGE_ACTION_NAMES = [
  core_apimessages_UserActionName.VIEW_HOW_TO_WATCH_CALLOUT,
  core_apimessages_UserActionName.SUBSEQUENT_LISTING_VIEW_CALLOUT,
  core_apimessages_UserActionName.VIEW_WATCH_HIATUS_CALLOUT,
] as const;

type ActionNameToI18nPrefix = {
  [key in typeof WATCH_BADGE_ACTION_NAMES[number]]: string;
};

const AVAILABLE_ACTION_NAME_TO_I18N_PREFIX: ActionNameToI18nPrefix = {
  [core_apimessages_UserActionName.VIEW_HOW_TO_WATCH_CALLOUT]: 'discovery.item2WatchBadge.howToWatchCallout',
  [core_apimessages_UserActionName.SUBSEQUENT_LISTING_VIEW_CALLOUT]: 'discovery.item2WatchBadge.subsequentListingViewCallout',
  [core_apimessages_UserActionName.VIEW_WATCH_HIATUS_CALLOUT]: 'discovery.item2WatchBadge.watchHiatusCallout',
};

interface ExternalProps {
  listing: WatchBadgeListing;
  availableActions: Item2AvailableActionsData.Fragment[];
  me: CreateUserActionMeData.Fragment;
}

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type Props = ExternalProps & MutationProps;

export function watchBadgeNextBestAction(actions: Item2AvailableActionsData.Fragment[]) {
  return actions?.find(action => includes(WATCH_BADGE_ACTION_NAMES, action.name) && action.status === core_apimessages_UserActionStatus.AVAILABLE);
}

export function Item2ImageWatchBadge(props: Props) {
  const offerButtonAction = availableOfferButtonAction(props.availableActions);
  const nextBestAction = watchBadgeNextBestAction(props.availableActions);
  const [isCalloutOpen, setIsCalloutOpen] = React.useState<boolean>(true);

  const createUserActionState = useCreateUserAction(
    nextBestAction?.name,
    props.me,
    {
      listingId: props.listing.id,
    },
    props.createUserAction,
  );

  let guidePopoverProps = {} as any;
  const shouldDisplayGuidePopover = !offerButtonAction && nextBestAction;
  if (shouldDisplayGuidePopover) {
    const i18nPrefix = AVAILABLE_ACTION_NAME_TO_I18N_PREFIX[nextBestAction.name];
    guidePopoverProps = {
      title: i18nPrefix ? I18n.t(`${i18nPrefix}.title`) : '',
      content: i18nPrefix ? I18n.t(`${i18nPrefix}.content`) : '',
      onDismiss: function () {
        setIsCalloutOpen(false);
        createUserActionState.createUserAction();
      },
      isOpen: isCalloutOpen,
    };
  }

  return (
    <WatchBadge
      listing={props.listing}
      displayStyle={DisplayStyle.ANIMATION_ICON}
      parentComponentName={WatchParentComponentNames.ItemPageImage}
      guidePopoverProps={guidePopoverProps}
      useToast
    />
  );
}

export default connect<ExternalProps>([
  withCreateUserActionMutation,
])(Item2ImageWatchBadge);
